export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  actionId?: string | null
  data?: any
  dataTotal?: number
  needsData?: boolean
  quotationType?: string
  workVisibility?: string
  isPublic?: boolean
  noResultsMessage?: string
  llmRawResponse?: string
  guideMode?: boolean
}

export interface ConversationSession {
  sessionId: string
  messages: Message[]
  createdAt: string
  lastUpdated: string
  messageCount: number
  isActive: boolean
}

export interface StoredConversations {
  sessions: ConversationSession[]
  currentSessionId: string | null
  maxSessions: number
}

const STORAGE_KEY_PREFIX = 'edify-assistant-conversations'
const DEFAULT_MAX_SESSIONS = 10

const getStorageKey = (userId?: string): string => {
  if (!userId) {
    return `${STORAGE_KEY_PREFIX}-guest`
  }
  return `${STORAGE_KEY_PREFIX}-user-${userId}`
}

export const createNewSession = (): ConversationSession => {
  const sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  return {
    sessionId,
    messages: [],
    createdAt: new Date().toISOString(),
    lastUpdated: new Date().toISOString(),
    messageCount: 0,
    isActive: true
  }
}

export const loadConversations = (userId?: string): StoredConversations => {
  try {
    const storageKey = getStorageKey(userId)
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const parsed = JSON.parse(stored) as StoredConversations
      return {
        ...parsed,
        maxSessions: parsed.maxSessions || DEFAULT_MAX_SESSIONS
      }
    }
  } catch (error) {
    console.error('Error loading conversations:', error)
  }

  return {
    sessions: [],
    currentSessionId: null,
    maxSessions: DEFAULT_MAX_SESSIONS
  }
}

export const saveConversations = (conversations: StoredConversations, userId?: string): void => {
  try {
    const storageKey = getStorageKey(userId)
    const cleaned = {
      ...conversations,
      sessions: conversations.sessions.slice(-conversations.maxSessions)
    }
    localStorage.setItem(storageKey, JSON.stringify(cleaned))
  } catch (error) {
    console.error('Error saving conversations:', error)
  }
}

export const getCurrentSession = (userId?: string): ConversationSession | null => {
  const conversations = loadConversations(userId)
  if (!conversations.currentSessionId) {
    return null
  }

  return conversations.sessions.find(
    s => s.sessionId === conversations.currentSessionId
  ) || null
}

export const saveSession = (session: ConversationSession, userId?: string): void => {
  const conversations = loadConversations(userId)
  
  const existingIndex = conversations.sessions.findIndex(
    s => s.sessionId === session.sessionId
  )

  if (existingIndex >= 0) {
    conversations.sessions[existingIndex] = {
      ...session,
      lastUpdated: new Date().toISOString()
    }
  } else {
    conversations.sessions.push({
      ...session,
      lastUpdated: new Date().toISOString()
    })
  }

  if (session.isActive) {
    conversations.currentSessionId = session.sessionId
  }

  saveConversations(conversations, userId)
}

export const addMessageToSession = (
  sessionId: string,
  message: Message,
  userId?: string
): ConversationSession | null => {
  const conversations = loadConversations(userId)
  const session = conversations.sessions.find(s => s.sessionId === sessionId)

  if (!session) {
    return null
  }

  const updatedSession: ConversationSession = {
    ...session,
    messages: [...session.messages, message],
    messageCount: session.messageCount + 1,
    lastUpdated: new Date().toISOString()
  }

  saveSession(updatedSession, userId)
  return updatedSession
}

export const setCurrentSession = (sessionId: string | null, userId?: string): void => {
  const conversations = loadConversations(userId)
  
  conversations.sessions.forEach(s => {
    s.isActive = s.sessionId === sessionId
  })

  conversations.currentSessionId = sessionId
  saveConversations(conversations, userId)
}

export const deleteSession = (sessionId: string, userId?: string): void => {
  const conversations = loadConversations(userId)
  conversations.sessions = conversations.sessions.filter(
    s => s.sessionId !== sessionId
  )

  if (conversations.currentSessionId === sessionId) {
    conversations.currentSessionId = null
  }

  saveConversations(conversations, userId)
}

export const clearAllSessions = (userId?: string): void => {
  const storageKey = getStorageKey(userId)
  localStorage.removeItem(storageKey)
}

